// src/pages/ProposalResponse.js
// Public page — client responds to a practitioner's reschedule proposal.
// Route: /booking/proposal/:bookingId/:token
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import LogoDark from '../assets/divine-frameworks-logo.svg';

const REGION = 'us-west1';

// ── Helpers ──

const formatDateTime = (dateTime, timezone) => {
  try {
    const date = dateTime?._seconds ? new Date(dateTime._seconds * 1000) : new Date(dateTime);
    return date.toLocaleString('en-US', {
      timeZone: timezone || 'UTC', weekday: 'long', year: 'numeric', month: 'long',
      day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch { return String(dateTime); }
};

const parseTime = (str) => { if (!str) return 0; const [h, m] = str.split(':').map(Number); return (h || 0) * 60 + (m || 0); };
const formatTime = (minutes) => { const h = Math.floor(minutes / 60); const m = minutes % 60; const ampm = h >= 12 ? 'PM' : 'AM'; return `${h === 0 ? 12 : h > 12 ? h - 12 : h}:${m.toString().padStart(2, '0')} ${ampm}`; };
const formatDateDisplay = (date) => date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

const getTimezoneOffset = (tz, date) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'longOffset' }).formatToParts(date);
    const offset = parts.find(p => p.type === 'timeZoneName')?.value || '';
    const match = offset.match(/GMT([+-]\d{1,2}(?::\d{2})?)/);
    if (match) { let v = match[1]; if (!v.includes(':')) v += ':00'; if (v.length === 5) v = v.replace(/^([+-])(\d):/, '$10$2:'); return v; }
    return '+00:00';
  } catch { return '+00:00'; }
};

const defaultAvailability = {
  schedule: {
    monday: { enabled: true, blocks: [{ start: '09:00', end: '17:00' }] },
    tuesday: { enabled: true, blocks: [{ start: '09:00', end: '17:00' }] },
    wednesday: { enabled: true, blocks: [{ start: '09:00', end: '17:00' }] },
    thursday: { enabled: true, blocks: [{ start: '09:00', end: '17:00' }] },
    friday: { enabled: true, blocks: [{ start: '09:00', end: '17:00' }] },
    saturday: { enabled: false, blocks: [] },
    sunday: { enabled: false, blocks: [] },
  },
  bufferTime: 15, bookingWindow: 30, minNotice: 24, timeOff: [],
};

// ── Component ──

const ProposalResponse = () => {
  const { bookingId, token } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('proposal'); // proposal | counter | accepted | declined | counter_sent
  const [processing, setProcessing] = useState(false);
  const [actionError, setActionError] = useState(null);

  // Counter-propose state
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const functions = getFunctions(undefined, REGION);
        const getBooking = httpsCallable(functions, 'getBookingByToken');
        const result = await getBooking({ bookingId, token });
        setBooking(result.data);

        const proposal = result.data.rescheduleProposal;
        if (!proposal) {
          setError('no_proposal');
        } else if (proposal.status === 'accepted' || proposal.counterProposal?.status === 'accepted') {
          setView('accepted');
        } else if (proposal.status === 'declined') {
          setView('declined');
        } else if (proposal.status === 'expired' || proposal.counterProposal?.status === 'expired') {
          setError('expired');
        } else if (proposal.status === 'counter_proposed') {
          setView('counter_sent');
        }
      } catch (err) {
        setError('generic');
      } finally {
        setLoading(false);
      }
    };
    fetchBooking();
  }, [bookingId, token]);

  const handleAction = async (action, counterDateTime) => {
    setProcessing(true);
    setActionError(null);
    try {
      const functions = getFunctions(undefined, REGION);
      const respond = httpsCallable(functions, 'respondToRescheduleProposal');
      // eslint-disable-next-line no-unused-vars
      const _result = await respond({
        bookingId, token, action,
        counterProposedDateTime: counterDateTime || null,
      });
      if (action === 'accept') setView('accepted');
      else if (action === 'decline') setView('declined');
      else if (action === 'counter_propose') setView('counter_sent');
    } catch (err) {
      if (err.message?.includes('expired')) setError('expired');
      else setActionError('Something went wrong. Please try again.');
      setProcessing(false);
    }
  };

  // Calendar helpers for counter-propose
  const availability = booking?.availability || defaultAvailability;
  const effectiveAvailability = availability?.schedule && Object.keys(availability.schedule).length > 0 ? availability : defaultAvailability;
  const timezone = availability?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const generateTimeSlots = (date) => {
    if (!date) return [];
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const daySchedule = effectiveAvailability?.schedule?.[dayNames[date.getDay()]];
    if (!daySchedule?.enabled || !daySchedule?.blocks?.length) return [];
    const slots = [];
    const serviceDuration = Number(booking?.service?.duration) || 60;
    const buffer = Number(effectiveAvailability?.bufferTime) || 15;
    const sixHoursFromNow = new Date(Date.now() + 6 * 3600000);
    daySchedule.blocks.forEach((block) => {
      let current = parseTime(block.start);
      const end = parseTime(block.end);
      while (current + serviceDuration <= end) {
        const slotDate = new Date(date);
        slotDate.setHours(Math.floor(current / 60), current % 60, 0, 0);
        if (slotDate >= sixHoursFromNow) slots.push({ time: current, label: formatTime(current) });
        current += serviceDuration + buffer;
      }
    });
    return slots;
  };

  const timeSlots = useMemo(() => generateTimeSlots(selectedDate), [selectedDate, booking?.service?.duration]);

  const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  const getFirstDayOfMonth = (y, m) => new Date(y, m, 1).getDay();
  const isDateAvailable = (date) => {
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    if (!effectiveAvailability?.schedule?.[dayNames[date.getDay()]]?.enabled) return false;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (date < today) return false;
    const windowDays = Number(effectiveAvailability?.bookingWindow) || 30;
    if (date > new Date(today.getTime() + windowDays * 86400000)) return false;
    return true;
  };
  const isSameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  const buildCounterISO = () => {
    if (!selectedDate || selectedTime === null) return null;
    const hours = Math.floor(selectedTime / 60);
    const mins = selectedTime % 60;
    const timeStr = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:00`;
    const tzDateStr = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(selectedDate);
    return `${tzDateStr}T${timeStr}${getTimezoneOffset(timezone, selectedDate)}`;
  };

  // ── Render helpers ──

  const renderLogo = () => (
    <div style={styles.logoContainer} onClick={() => navigate('/')}>
      <img src={LogoDark} alt="Divine Frameworks" style={styles.logo} />
      <span style={styles.logoText}>Divine Frameworks</span>
    </div>
  );

  // ── Loading ──
  if (loading) {
    return (
      <div style={styles.page}><div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div style={styles.spinner} />
          <p style={{ color: '#6b7280', fontSize: '15px', marginTop: '16px' }}>Loading proposal...</p>
        </div>
      </div></div>
    );
  }

  // ── Error states ──
  if (error) {
    const messages = {
      expired: "This reschedule proposal has expired. Your original session is still scheduled.",
      no_proposal: "There's no active reschedule proposal for this booking.",
      generic: "Something went wrong. Please try again or contact support@divineframeworks.com.",
    };
    return (
      <div style={styles.page}><div style={styles.container}>
        {renderLogo()}
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>{error === 'expired' ? '⏰' : '🔗'}</div>
          <p style={{ color: '#374151', fontSize: '16px', lineHeight: 1.6, maxWidth: '400px', margin: '0 auto' }}>{messages[error]}</p>
        </div>
      </div></div>
    );
  }

  const proposal = booking?.rescheduleProposal;
  const tz = booking?.timezone || 'UTC';
  const currentFormatted = formatDateTime(booking?.dateTime, tz);
  const proposedFormatted = formatDateTime(proposal?.proposedDateTime, tz);
  const deadlineFormatted = formatDateTime(proposal?.expiresAt, tz);
  const isExpiringSoon = proposal?.expiresAt && (new Date(proposal.expiresAt) - Date.now()) < 2 * 3600000;

  // ── Accepted ──
  if (view === 'accepted') {
    const newTime = proposal?.proposedDateTime || proposal?.counterProposal?.proposedDateTime;
    return (
      <div style={styles.page}><div style={styles.container}>
        {renderLogo()}
        <div style={{ textAlign: 'center', padding: '40px 0', animation: 'prFadeIn 0.4s ease' }}>
          <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: '24px' }}>✓</div>
          <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#111827', marginBottom: '12px' }}>Your session has been rescheduled</h2>
          <p style={{ fontSize: '17px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>{formatDateTime(newTime, tz)}</p>
          <p style={{ fontSize: '15px', color: '#6b7280' }}>{booking?.service?.name} with {booking?.practitionerName}</p>
        </div>
      </div>
      <style>{`@keyframes prFadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }`}</style>
      </div>
    );
  }

  // ── Declined ──
  if (view === 'declined') {
    return (
      <div style={styles.page}><div style={styles.container}>
        {renderLogo()}
        <div style={{ textAlign: 'center', padding: '40px 0', animation: 'prFadeIn 0.4s ease' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#111827', marginBottom: '12px' }}>You've kept your original session</h2>
          <p style={{ fontSize: '17px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>{currentFormatted}</p>
          <p style={{ fontSize: '15px', color: '#6b7280' }}>{booking?.service?.name} with {booking?.practitionerName}</p>
        </div>
      </div>
      <style>{`@keyframes prFadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }`}</style>
      </div>
    );
  }

  // ── Counter sent ──
  if (view === 'counter_sent') {
    const counterExpires = proposal?.counterProposal?.expiresAt;
    return (
      <div style={styles.page}><div style={styles.container}>
        {renderLogo()}
        <div style={{ textAlign: 'center', padding: '40px 0', animation: 'prFadeIn 0.4s ease' }}>
          <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: '24px' }}>📤</div>
          <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#111827', marginBottom: '12px' }}>Your suggested time has been sent</h2>
          <p style={{ fontSize: '15px', color: '#6b7280', marginBottom: '8px' }}>{booking?.practitionerName} will respond by:</p>
          <p style={{ fontSize: '15px', fontWeight: 600, color: '#111827' }}>{counterExpires ? formatDateTime(counterExpires, tz) : 'soon'}</p>
          <p style={{ fontSize: '14px', color: '#9CA3AF', marginTop: '16px' }}>If they don't respond in time, your original session stays as scheduled.</p>
        </div>
      </div>
      <style>{`@keyframes prFadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }`}</style>
      </div>
    );
  }

  // ── Counter-propose view ──
  if (view === 'counter') {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const monthLabel = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    return (
      <div style={styles.page}><div style={styles.container}>
        {renderLogo()}
        <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>Suggest a different time</h2>
        <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px' }}>Choose a time that works for you.</p>

        {/* Calendar */}
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '10px', padding: '16px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <button onClick={() => setCurrentMonth(new Date(year, month - 1, 1))} style={styles.calNavBtn}><ChevronLeft size={18} /></button>
            <span style={{ fontSize: '15px', fontWeight: 600, color: '#111827' }}>{monthLabel}</span>
            <button onClick={() => setCurrentMonth(new Date(year, month + 1, 1))} style={styles.calNavBtn}><ChevronRight size={18} /></button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', textAlign: 'center' }}>
            {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
              <div key={d} style={{ fontSize: '11px', fontWeight: 600, color: '#9CA3AF', padding: '4px' }}>{d}</div>
            ))}
            {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} />)}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const date = new Date(year, month, day);
              const available = isDateAvailable(date);
              const selected = isSameDay(date, selectedDate);
              return (
                <button key={day} disabled={!available} onClick={() => { setSelectedDate(date); setSelectedTime(null); }}
                  style={{ padding: '8px 4px', borderRadius: '8px', border: 'none', fontSize: '13px', fontWeight: 500, cursor: available ? 'pointer' : 'default', background: selected ? '#bb9749' : 'transparent', color: selected ? '#fff' : available ? '#111827' : '#D1D5DB', transition: 'all 0.15s ease' }}
                >{day}</button>
              );
            })}
          </div>
        </div>

        {/* Time slots */}
        {selectedDate && (
          <div style={{ marginBottom: '24px' }}>
            <p style={{ fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '8px' }}>Available times for {formatDateDisplay(selectedDate)}</p>
            {timeSlots.length === 0 ? (
              <p style={{ fontSize: '13px', color: '#9CA3AF' }}>No available times on this date.</p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                {timeSlots.map(slot => (
                  <button key={slot.time} onClick={() => setSelectedTime(slot.time)}
                    style={{ padding: '10px', borderRadius: '8px', fontSize: '13px', fontWeight: 500, border: selectedTime === slot.time ? '2px solid #bb9749' : '1px solid #E5E7EB', background: selectedTime === slot.time ? '#FEF3C7' : '#fff', color: '#111827', cursor: 'pointer', transition: 'all 0.15s ease' }}
                  >{slot.label}</button>
                ))}
              </div>
            )}
          </div>
        )}

        {actionError && (
          <div style={{ padding: '12px 16px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', marginBottom: '16px' }}>
            <p style={{ fontSize: '14px', color: '#991B1B', margin: 0 }}>{actionError}</p>
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px' }}>
          <span role="button" tabIndex={0} onClick={() => { setView('proposal'); setSelectedDate(null); setSelectedTime(null); }} onKeyDown={(e) => e.key === 'Enter' && setView('proposal')} style={{ ...styles.secondaryBtn, userSelect: 'none' }}>Cancel</span>
          <button disabled={!selectedDate || selectedTime === null || processing} onClick={() => handleAction('counter_propose', buildCounterISO())}
            style={{ ...styles.goldBtn, opacity: (!selectedDate || selectedTime === null || processing) ? 0.4 : 1, cursor: (!selectedDate || selectedTime === null || processing) ? 'not-allowed' : 'pointer' }}
          >{processing ? 'Sending...' : 'Send suggestion'}</button>
        </div>
      </div>
      <style>{`@keyframes prFadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }`}</style>
      </div>
    );
  }

  // ── Main proposal view ──
  return (
    <div style={styles.page}><div style={styles.container}>
      {renderLogo()}

      <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#111827', marginBottom: '16px' }}>
        {booking?.practitionerName} would like to reschedule your session
      </h2>

      {/* Practitioner message */}
      {proposal?.message && (
        <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '16px 20px', marginBottom: '20px' }}>
          <p style={{ fontSize: '14px', fontStyle: 'italic', color: '#374151', margin: '0 0 6px' }}>"{proposal.message}"</p>
          <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>— {booking?.practitionerName}</p>
        </div>
      )}

      {/* Comparison cards */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
        <div style={{ flex: 1, background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '10px', padding: '14px 16px' }}>
          <p style={{ fontSize: '11px', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 6px' }}>Current</p>
          <p style={{ fontSize: '14px', fontWeight: 600, color: '#6B7280', margin: '0 0 4px' }}>{booking?.service?.name}</p>
          <p style={{ fontSize: '13px', color: '#9CA3AF', textDecoration: 'line-through' }}>{currentFormatted}</p>
        </div>
        <div style={{ flex: 1, background: '#FFFBEB', border: '1px solid #FDE68A', borderLeft: '3px solid #bb9749', borderRadius: '10px', padding: '14px 16px' }}>
          <p style={{ fontSize: '11px', fontWeight: 600, color: '#bb9749', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 6px' }}>Proposed</p>
          <p style={{ fontSize: '14px', fontWeight: 600, color: '#111827', margin: '0 0 4px' }}>{booking?.service?.name}</p>
          <p style={{ fontSize: '13px', color: '#374151' }}>{proposedFormatted}</p>
        </div>
      </div>

      {/* Warm context line */}
      <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: 1.6, marginBottom: '16px' }}>
        We understand schedule changes can be inconvenient. {booking?.practitionerName} wants to find a time that works for both of you.
      </p>

      {/* Deadline */}
      <p style={{ fontSize: '13px', color: isExpiringSoon ? '#DC2626' : '#6B7280', marginBottom: '24px' }}>
        ⏱ Please respond by <strong>{deadlineFormatted}</strong>
      </p>

      {actionError && (
        <div style={{ padding: '12px 16px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', marginBottom: '16px' }}>
          <p style={{ fontSize: '14px', color: '#991B1B', margin: 0 }}>{actionError}</p>
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <button onClick={() => handleAction('accept')} disabled={processing}
          style={{ ...styles.goldBtn, opacity: processing ? 0.7 : 1 }}
        >{processing ? 'Processing...' : 'Accept new time'}</button>
        <button onClick={() => setView('counter')} disabled={processing}
          style={{ ...styles.outlinedGoldBtn, opacity: processing ? 0.5 : 1 }}
        >Suggest a different time</button>
        <span role="button" tabIndex={0} onClick={() => !processing && navigate(`/booking/manage/${bookingId}/${token}?action=cancel`)} onKeyDown={(e) => e.key === 'Enter' && !processing && navigate(`/booking/manage/${bookingId}/${token}?action=cancel`)}
          style={{ ...styles.dangerBtn, opacity: processing ? 0.5 : 1, userSelect: 'none' }}
        >Cancel this session</span>
      </div>
    </div>
    <style>{`
      @keyframes prFadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
      @keyframes prSpin { to { transform: rotate(360deg); } }
    `}</style>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh', background: '#F9FAFB', display: 'flex', justifyContent: 'center',
    padding: '40px 20px', fontFamily: "'Montserrat', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  container: { width: '100%', maxWidth: '520px' },
  logoContainer: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '32px', cursor: 'pointer' },
  logo: { height: '48px', width: 'auto' },
  logoText: { fontSize: '22px', fontWeight: 700, color: '#1f2937', fontFamily: "'Montserrat', sans-serif" },
  goldBtn: {
    width: '100%', padding: '14px 24px', background: '#bb9749', color: '#fff',
    border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 600,
    cursor: 'pointer', fontFamily: "'Montserrat', sans-serif", transition: 'opacity 0.2s ease',
  },
  outlinedGoldBtn: {
    width: '100%', padding: '14px 24px', background: 'transparent', color: '#bb9749',
    border: '1.5px solid #bb9749', borderRadius: '8px', fontSize: '15px', fontWeight: 600,
    cursor: 'pointer', fontFamily: "'Montserrat', sans-serif", transition: 'all 0.2s ease',
  },
  subtleBtn: {
    width: '100%', padding: '14px 24px', background: 'transparent', color: '#6B7280',
    border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 500,
    cursor: 'pointer', fontFamily: "'Montserrat', sans-serif",
  },
  dangerBtn: {
    width: '100%', padding: '14px 24px', background: 'transparent', color: '#DC2626',
    border: '1px solid #FCA5A5', borderRadius: '8px', fontSize: '15px', fontWeight: 500,
    cursor: 'pointer', fontFamily: "'Montserrat', sans-serif",
  },
  secondaryBtn: {
    flex: 1, padding: '14px 24px', background: 'transparent', color: '#374151',
    border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '15px', fontWeight: 500,
    cursor: 'pointer', fontFamily: "'Montserrat', sans-serif",
  },
  calNavBtn: {
    background: 'none', border: '1px solid #E5E7EB', borderRadius: '8px',
    padding: '6px', cursor: 'pointer', color: '#374151', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
  },
  spinner: {
    width: '32px', height: '32px', border: '3px solid #E5E7EB', borderTopColor: '#bb9749',
    borderRadius: '50%', animation: 'prSpin 0.8s linear infinite', margin: '0 auto',
  },
};

export default ProposalResponse;
